import React from 'react'
import { Outlet } from 'react-router-dom'

import { ReportsRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import type { Back } from 'modules/core/components/Card/types'
import CrashLogo from 'modules/core/components/CrashLogo'
import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import CollectionsBootstrap from 'modules/reports/lib/CollectionsBootstrap'
import conditions from 'modules/reports/store/collections/conditions'
import dashboards from 'modules/reports/store/collections/dashboards'
import favouriteDashboards from 'modules/reports/store/collections/favourite_dashboards'
import fields from 'modules/reports/store/collections/fields'
import filters from 'modules/reports/store/collections/filters'
import projections from 'modules/reports/store/collections/projections'
import reports from 'modules/reports/store/collections/reports'

const Container = () => {
  const { navigation } = useNavigation()

  const [hasFailedFetching, setHasFailedFetching] = React.useState(false)
  const { loading } = useAsyncData(() => {
    const fetchs = [
      dashboards.isEmpty ? dashboards.fetch() : Promise.resolve(),
      favouriteDashboards.isEmpty ? favouriteDashboards.fetch() : Promise.resolve(),
      reports.isEmpty ? reports.fetch() : Promise.resolve(),
      fields.isEmpty ? fields.fetch() : Promise.resolve(),
      projections.isEmpty ? projections.fetch() : Promise.resolve(),
      filters.isEmpty ? filters.fetch() : Promise.resolve(),
      conditions.isEmpty ? conditions.fetch() : Promise.resolve(),
    ]
    return Promise.all(fetchs)
      .then(() =>
        Promise.resolve([
          dashboards,
          favouriteDashboards,
          reports,
          fields,
          projections,
          filters,
          conditions,
        ])
      ) // Promise.all resolved
      .catch(() => {
        setHasFailedFetching(true)
        return Promise.resolve()
      }) // Promise.all has at least one rejection
  }, [])

  const back: Back = {
    href: navigation.reports.fullPath(),
    label: i18n.t('custom_reports.editor.back'),
  }

  if (hasFailedFetching) {
    return <CrashLogo compact />
  }

  return <Outlet context={{ back, loading }} />
}

const RootPage = () => {
  const { data, loading } = useQuery(ReportsRootPageDocument)

  if (loading) {
    return <Loading />
  }

  return (
    <AuthorizeUser authorizeFn={() => data?.reports?._policies?.useReports.can ?? false}>
      <CollectionsBootstrap>
        <Container />
      </CollectionsBootstrap>
    </AuthorizeUser>
  )
}

export default RootPage
